import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Dashboard from './pages/Dashboard';
import Books from './pages/Books';
import Members from './pages/Members';
import Settings from './pages/Settings';
import Login from './pages/Login';
import ScanModal from './components/ScanModal';
import { setBooks, setScanModalOpen, fetchCategories } from './store/slices/booksSlice';
import { setUser, setIsLoggedIn, setSettings } from './store/slices/userSlice';
import { setMembers } from './store/slices/membersSlice';
import getBooks from './api/GetBooks';
import getUsers from './api/GetUsers';
import getSettings from './api/GetSettings';
import LoginUser from './api/LoginUser';

function App() {
  const [activeTab, setActiveTab] = useState('Dashboard');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.user.currentUser);
  const isLoggedIn = currentUser.isLoggedIn;
  const API_BASE = useSelector((state) => state.user.apiBase);
  const token = useSelector((state) => state.user.currentUser.token);
  const code_etablissement = useSelector((state) => state.user.currentUser.code_etablissement);
  const { roleAvatars } = useSelector((state) => state.members);
  const isScanModalOpen = useSelector((state) => state.books.isScanModalOpen);

  useEffect(() => {
    if (isLoggedIn) {
      console.log("App.jsx: User is logged in, fetching data...", { API_BASE, token, code_etablissement });

      if (!API_BASE || !token || !code_etablissement) {
        console.warn("App.jsx: Missing credentials for data fetch", { API_BASE, token, code_etablissement });
        return;
      }
      getSettings(API_BASE, token, code_etablissement)
        .then((data) => {
          data.map((setting) => {
            if (setting.nom === "delai-emprunt") {
              dispatch(setSettings({ delaiEmprunt: setting.valeur }));
            }
            if (setting.nom === "late-return-email-template") {
              dispatch(setSettings({ lateReturnEmailTemplate: setting.valeur }));
            }
          });
          console.log("App.jsx: Settings fetched successfully", data.settings.length);
        })
        .catch(err => console.error("App.jsx: Error fetching settings:", err));
      getBooks(API_BASE, token, code_etablissement)
        .then((data) => {
          if (data && data.work) {
            console.log("App.jsx: Books fetched successfully", data.work.length);
            dispatch(setBooks(data.work));
          }
        })
        .catch(err => console.error("App.jsx: Error fetching books:", err));

      getUsers(API_BASE, token, code_etablissement, roleAvatars)
        .then((data) => {
          if (data && data.users) {
            console.log("App.jsx: Users fetched successfully", data.users.length);
            dispatch(setMembers(data.users));
          }
        })
        .catch(err => console.error("App.jsx: Error fetching users:", err));

      // Fetch categories
      dispatch(fetchCategories({ API_BASE, token, code_etablissement }))
        .unwrap()
        .then(() => console.log("App.jsx: Categories fetched successfully"))
        .catch(err => console.error("App.jsx: Error fetching categories:", err));
    }
  }, [isLoggedIn, API_BASE, token, code_etablissement, dispatch, roleAvatars]);

  return (
    <div className="min-h-screen bg-background text-foreground selection:bg-primary/30 selection:text-primary-foreground">
      {/* Background Gradients */}
      <div style={{ display: "block" }} className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-primary/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-purple-500/10 rounded-full blur-[120px]" />
      </div>

      {!isLoggedIn ? (
        <Login
          onLogin={async (email, password) => {
            try {
              const rawUserData = await LoginUser(API_BASE, email, password);
              if (rawUserData) {
                // Normalize data (backend might use Token instead of token)
                const userData = {
                  ...rawUserData,
                  token: rawUserData.token,
                  role: rawUserData.role || rawUserData.Role || "Bibliothécaire",
                  avatar: rawUserData.avatar || roleAvatars[rawUserData.role || rawUserData.Role] || "/avatars/librarian.png",
                  code_etablissement: rawUserData.code_etablissement || rawUserData.CodeEtablissement || "ESHS1", // fallback if missing
                  isLoggedIn: true
                };

                console.log("Login successful, normalized data:", userData);
                dispatch(setUser(userData));
              }
            } catch (error) {
              console.error("Login failed:", error);
              alert("Erreur de connexion : " + error.message);
            }
          }}
          onGuestLogin={(selectedCode) => {
            const guestData = {
              name: "invité",
              role: "invité",
              avatar: "/avatars/student.png",
              code_etablissement: selectedCode,
              token: "GUEST_TOKEN",
              isLoggedIn: true
            };
            console.log("Guest login successful:", guestData);
            dispatch(setUser(guestData));
            setActiveTab('Books');
          }}
        />
      ) : (
        <>
          {currentUser.role !== "invité" && (
            <Sidebar
              activeItem={activeTab}
              setActiveItem={setActiveTab}
              isOpen={isSidebarOpen}
              setIsOpen={setIsSidebarOpen}
              onLogout={() => dispatch(setIsLoggedIn(false))}
            />
          )}
          <Header activeTab={activeTab} onMenuClick={() => setIsSidebarOpen(true)} />

          <main className={`${currentUser.role !== "invité" ? "lg:ml-72" : ""} p-4 sm:p-6 lg:p-8 relative`}>
            <div className="max-w-7xl mx-auto">
              {activeTab === 'Dashboard' && <Dashboard onNavigate={setActiveTab} />}
              {activeTab === 'Books' && <Books />}
              {activeTab === 'Members' && <Members />}
              {activeTab === 'Settings' && <Settings />}
            </div>
          </main>
          <ScanModal
            isOpen={isScanModalOpen}
            onClose={() => dispatch(setScanModalOpen(false))}
            onBookFound={() => setActiveTab('Books')}
            onMemberFound={() => setActiveTab('Members')}
          />
        </>
      )}
    </div>
  );
}

export default App;
